import type { Markup, Point } from '@/types';
import type { Box, CanonicalMarkupGeometry } from './types';
import { resolveMarkupFontSize } from '@/core/markups/normalizeMarkup';
import { TEXT_LINE_HEIGHT_RATIO, TEXT_CHAR_WIDTH_RATIO } from '@/utils/text-metrics';

function boxFromPoints(points: Point[]): Box | null {
    if (points.length === 0) return null;

    const xs = points.map((point) => point.x);
    const ys = points.map((point) => point.y);
    return {
        left: Math.min(...xs),
        top: Math.min(...ys),
        right: Math.max(...xs),
        bottom: Math.max(...ys),
    };
}

function estimateTextBox(markup: Extract<Markup, { type: 'text' }>): Box | null {
    const anchor = markup.points[0];
    if (!anchor) return null;

    const text = markup.text || 'Text';
    const lines = text.split('\n');
    const lineCount = lines.length;
    const maxLineLength = Math.max(...lines.map((line) => line.length), 4);
    const fontSize = resolveMarkupFontSize(markup.style);
    const width = maxLineLength * fontSize * TEXT_CHAR_WIDTH_RATIO;
    const height = lineCount * fontSize * TEXT_LINE_HEIGHT_RATIO;

    return {
        left: anchor.x,
        top: anchor.y - fontSize,
        right: anchor.x + width,
        bottom: anchor.y + height - fontSize,
    };
}

export function normalizeMarkupGeometry(markup: Markup): CanonicalMarkupGeometry | null {
    if (!markup.points?.length) return null;

    switch (markup.type) {
        case 'line':
        case 'arrow':
        case 'move':
            return markup.points.length >= 2
                ? { kind: 'segment', a: markup.points[0], b: markup.points[1] }
                : null;
        case 'polyline':
        case 'freehand':
            return markup.points.length >= 2
                ? { kind: 'polyline', points: [...markup.points] }
                : null;
        case 'polygon':
            return markup.points.length >= 3
                ? { kind: 'polygon', points: [...markup.points] }
                : null;
        case 'rectangle':
        case 'highlight':
        case 'cloud':
        case 'stamp':
        case 'snapshot':
        case 'image': {
            // snapshot and image, like rectangle, occupy a bounding box (captured content / the photo drawn inside).
            const box = boxFromPoints(markup.points);
            return box ? { kind: 'box', box } : null;
        }
        case 'ellipse': {
            const box = boxFromPoints(markup.points);
            return box ? { kind: 'ellipse', box } : null;
        }
        case 'count':
        case 'photo-pin':
            return markup.points[0] ? { kind: 'point', point: markup.points[0] } : null;
        case 'callout':
            if (markup.points.length < 3) return null;
            return {
                kind: 'callout',
                tip: markup.points[0],
                box: boxFromPoints(markup.points.slice(1))!,
            };
        case 'text': {
            const box = estimateTextBox(markup);
            return box ? { kind: 'text', anchor: markup.points[0], estimatedBox: box } : null;
        }
        case 'group': {
            const box = boxFromPoints(markup.points);
            return box ? { kind: 'group', box } : null;
        }
        case 'dimension':
            return markup.points.length >= 3
                ? {
                    kind: 'dimension',
                    a: markup.points[0],
                    b: markup.points[1],
                    offset: markup.points[2],
                }
                : markup.points.length >= 2
                    ? {
                        kind: 'segment',
                        a: markup.points[0],
                        b: markup.points[1],
                    }
                    : null;
        default:
            return null;
    }
}

export function boxToPoints(box: Box): Point[] {
    return [
        { x: box.left, y: box.top },
        { x: box.right, y: box.top },
        { x: box.right, y: box.bottom },
        { x: box.left, y: box.bottom },
    ];
}
